// frontend/src/components/AdminLayout.jsx
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
    HomeIcon,
    UsersIcon,
    CreditCardIcon,
    BuildingLibraryIcon,
    Cog6ToothIcon,
    ArrowLeftOnRectangleIcon,
    GlobeAltIcon,
    InboxIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';

// ── Admin Path from Environment ────────────────────────────────────
const ADMIN_PATH = import.meta.env.VITE_ADMIN_PATH || 'gmc-secure-dashboard-7x9k2m';

const navigation = [
    { name: 'Dashboard', href: `/${ADMIN_PATH}`, icon: HomeIcon, end: true },
    { name: 'Users', href: `/${ADMIN_PATH}/users`, icon: UsersIcon },
    { name: 'Plans', href: `/${ADMIN_PATH}/plans`, icon: BuildingLibraryIcon },
    { name: 'Billing', href: `/${ADMIN_PATH}/billing`, icon: CreditCardIcon },
    { name: 'Submissions', href: `/${ADMIN_PATH}/submissions`, icon: InboxIcon },
    { name: 'Settings', href: `/${ADMIN_PATH}/settings`, icon: Cog6ToothIcon },
];

export default function AdminLayout() {
    const navigate = useNavigate();
    const location = useLocation();
    const { logout } = useAuth();

    const handleLogout = () => {
        if (window.confirm('Are you sure you want to logout?')) {
            logout();
            navigate('/login');
        }
    };

    const goHome = () => {
        navigate('/');
    };

    const isActive = (item) =>
        item.end ? location.pathname === item.href : location.pathname.startsWith(item.href);

    return (
        <div className="min-h-screen bg-brand-navy">
            {/* Sidebar */}
            <div className="fixed inset-y-0 left-0 w-64 bg-brand-deep border-r border-brand-border z-10">
                <div className="flex flex-col h-full">
                    {/* Brand */}
                    <div className="p-6 border-b border-brand-border">
                        <h1 className="font-display text-xl font-bold text-brand-text">GMC Admin</h1>
                        <p className="text-sm text-brand-muted font-body">Secure Panel</p>
                    </div>

                    {/* Navigation */}
                    <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
                        {navigation.map((item) => {
                            const active = isActive(item);
                            return (
                                <Link
                                    key={item.name}
                                    to={item.href}
                                    className={`flex items-center px-4 py-2.5 text-sm font-medium rounded-lg transition-colors font-body ${active
                                        ? 'bg-brand-amber/10 text-brand-amber'
                                        : 'text-brand-muted hover:bg-brand-surface hover:text-brand-text'
                                        }`}
                                >
                                    <item.icon
                                        className={`h-5 w-5 mr-3 ${active ? 'text-brand-amber' : 'text-brand-muted'}`}
                                    />
                                    {item.name}
                                </Link>
                            );
                        })}
                    </nav>

                    {/* Footer Actions */}
                    <div className="p-4 border-t border-brand-border space-y-1">
                        {/* Home Button */}
                        <button
                            onClick={goHome}
                            className="flex items-center w-full px-4 py-2.5 text-sm font-medium text-brand-muted rounded-lg hover:bg-brand-surface hover:text-brand-text transition-colors font-body"
                        >
                            <GlobeAltIcon className="h-5 w-5 mr-3 text-brand-muted" />
                            Visit Website
                        </button>

                        {/* Logout Button */}
                        <button
                            onClick={handleLogout}
                            className="flex items-center w-full px-4 py-2.5 text-sm font-medium text-red-400 rounded-lg hover:bg-red-500/10 transition-colors font-body"
                        >
                            <ArrowLeftOnRectangleIcon className="h-5 w-5 mr-3 text-red-400" />
                            Logout
                        </button>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="pl-64">
                <div className="p-8">
                    <Outlet />
                </div>
            </div>
        </div>
    );
}